/**
 * The invoicing screens' shared pieces, in the same visual language as `pages/orders/Detail.tsx`:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/social-media/dashboard/recent-activities.tsx
 *   `AuditFeed` — the activity rows, one per audit entry, newest first;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   `TotalsCard` — the balance block under the items, here as the side column's lead card;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   `PartyBlock` — icon tile + label/value, for a supplier or recipient.
 *
 * Every figure is the API's (constitution I): the totals card prints the fields Billing sent, and
 * nothing is added, subtracted or compared into a new number.
 */
import type { ReactNode } from "react";
import {
  PiArrowRightBold as ArrowRight,
  PiBuildingsDuotone as BuildingIcon,
  PiCheckBold as Check,
  PiClockCounterClockwiseDuotone as HistoryIcon,
  PiCurrencyInrDuotone as RupeeIcon,
  PiXCircleDuotone as CancelIcon,
} from "react-icons/pi";
import { Badge, Text } from "rizzui";
import { Link } from "react-router-dom";
import { ComplianceStatusBadge, EmptyState, WidgetCard, cn, type ComplianceState, type ComplianceSubject } from "@steelyard/theme";
import { CardTitle, InfoBlock } from "../detail-parts";
import { formatMoment } from "../../lib/format";
import { money, statusWords, type AuditEntry } from "../../lib/services/invoicing";

/** A compliance status as its badge; a word the map does not know is shown verbatim, never guessed at. */
export function ComplianceBadge({ subject, status, map }: { subject: ComplianceSubject; status: string; map: Readonly<Record<string, ComplianceState>> }) {
  const state = map[status];
  return state ? (
    <ComplianceStatusBadge subject={subject} state={state} />
  ) : (
    <Badge variant="outline" rounded="pill">
      {statusWords(status)}
    </Badge>
  );
}

export function SummaryRow({ label, value, muted }: { label: ReactNode; value: ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className={cn("font-medium tabular-nums", muted ? "text-gray-500" : "text-gray-900")}>{value}</dd>
    </div>
  );
}

/** The document's own totals, in the gradient-headed balance card the order screen uses. */
export function TotalsCard({
  heading,
  total,
  caption,
  doc,
  footer,
}: {
  heading: string;
  total: string;
  caption: ReactNode;
  doc: { grossValue?: number; discount?: number; taxableValue: number; cgst: number; sgst: number; igst: number; deliveryCharge?: number; roundOff: number; grandTotal: number };
  footer?: ReactNode;
}) {
  return (
    <section aria-label={heading} className="overflow-hidden rounded-xl border border-muted bg-gray-0 dark:bg-gray-50">
      <div className="bg-gradient-to-br from-secondary to-blue px-6 py-5 text-white">
        <Text className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/85">
          <RupeeIcon aria-hidden className="size-4" />
          {heading}
        </Text>
        <Text className="mt-2 font-lexend text-3xl font-bold tabular-nums">{total}</Text>
        <Text className="mt-1 text-sm text-white/85">{caption}</Text>
      </div>
      <dl className="flex flex-col gap-3 px-6 py-5 text-sm">
        {/* D-39: the trade discount, shown rather than absorbed — gross, less the discount, is the taxable value. */}
        {doc.discount ? (
          <>
            <SummaryRow label="Gross value" value={money(doc.grossValue ?? doc.taxableValue)} />
            <SummaryRow label="Less trade discount" value={<span className="text-green-dark">−{money(doc.discount)}</span>} />
          </>
        ) : null}
        <SummaryRow label="Taxable value" value={money(doc.taxableValue)} />
        <SummaryRow label="CGST" value={money(doc.cgst)} muted={!doc.cgst} />
        <SummaryRow label="SGST" value={money(doc.sgst)} muted={!doc.sgst} />
        <SummaryRow label="IGST" value={money(doc.igst)} muted={!doc.igst} />
        {/* D-31: the order's freight, invoiced untaxed beside the lines. */}
        {doc.deliveryCharge ? <SummaryRow label="Delivery charge (no GST)" value={money(doc.deliveryCharge)} /> : null}
        {/* §8.5 step 6: the residue to the nearest rupee, always shown — including when it is nil. */}
        <SummaryRow label="Round off" value={money(doc.roundOff)} muted={!doc.roundOff} />
        <div className="flex items-baseline justify-between gap-4 border-t border-dashed border-muted pt-3">
          <dt className="text-base font-semibold text-gray-900">Grand total</dt>
          <dd className="font-lexend text-lg font-bold tabular-nums text-gray-900">{money(doc.grandTotal)}</dd>
        </div>
      </dl>
      {footer}
    </section>
  );
}

/** One party on a GST document, exactly as Billing recorded it; a missing GSTIN is said in words. */
export function PartyBlock({ label, tile, name, gstin, noGstin, address }: { label: string; tile: string; name: string | null; gstin: string | null; noGstin: string; address: string | null }) {
  return (
    <InfoBlock icon={<BuildingIcon aria-hidden className="size-4" />} tile={tile} label={label}>
      <Text className="font-semibold text-gray-900">{name ?? "Not recorded"}</Text>
      <Text className={cn("mt-0.5 font-mono text-xs", gstin ? "text-gray-700" : "text-gray-500")}>{gstin ? `GSTIN ${gstin}` : noGstin}</Text>
      {address ? <Text className="mt-1 text-sm leading-relaxed text-gray-600">{address}</Text> : null}
    </InfoBlock>
  );
}

/** The audit trail as an activity feed, newest first. `limit` shows the latest few with a link to the rest. */
export function AuditFeed({ entries, limit, allHref, title = "Activity" }: { entries: readonly AuditEntry[]; limit?: number; allHref?: string; title?: string }) {
  // Sorted here rather than trusting the API's order: its route has answered newest-first while its
  // docs said oldest-first, and a feed that marks the oldest entry "Latest" is worse than no feed.
  const newestFirst = [...entries].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  const shown = limit ? newestFirst.slice(0, limit) : newestFirst;

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<HistoryIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
          {title}
        </CardTitle>
      }
      action={
        entries.length > 0 ? (
          <Badge variant="flat" color="warning" rounded="pill">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </Badge>
        ) : null
      }
      headerClassName="items-center"
    >
      {shown.length === 0 ? (
        <div className="pt-5">
          <EmptyState title="No entries yet">No changes have been recorded against this document yet.</EmptyState>
        </div>
      ) : (
        <ol className="mt-6 flex flex-col">
          {shown.map((entry, index) => {
            const latest = index === 0;
            const cancelled = entry.action.includes("cancel");
            return (
              <li key={entry.id || `${entry.occurredAt}-${index}`} className="group relative flex gap-4 pb-6 last:pb-0">
                {index < shown.length - 1 ? <span aria-hidden className="absolute bottom-0 start-[17px] top-10 w-0.5 bg-muted" /> : null}
                <span
                  className={cn(
                    "relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full ring-4 ring-gray-0 dark:ring-gray-50",
                    cancelled ? "bg-red text-white" : latest ? "bg-secondary text-white shadow-md" : "bg-green-lighter text-green-dark",
                  )}
                >
                  {cancelled ? <CancelIcon aria-hidden className="size-4" /> : latest ? <ArrowRight aria-hidden className="size-4" /> : <Check aria-hidden className="size-4" />}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg px-3 py-1.5 transition-colors group-hover:bg-gray-50 dark:group-hover:bg-gray-100/40">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-semibold capitalize text-gray-900">
                      {statusWords(entry.action)}
                      {latest ? (
                        <Badge variant="flat" color="secondary" size="sm" rounded="pill" className="normal-case">
                          Latest
                        </Badge>
                      ) : null}
                    </span>
                    <Text as="span" className="text-xs text-gray-500">
                      {formatMoment(entry.occurredAt)}
                    </Text>
                  </div>
                  {entry.reason ? <Text className="rounded-md border-s-2 border-muted bg-gray-50 px-3 py-2 text-sm italic text-gray-700 dark:bg-gray-100/40">“{entry.reason}”</Text> : null}
                  <Text className="text-xs text-gray-500">
                    by {entry.actorComponent ?? (entry.actorId ? (entry.actorName ?? "Unknown user") : "an actor not recorded")}
                  </Text>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {allHref && limit && entries.length > limit ? (
        <div className="mt-5 border-t border-muted pt-4 text-center">
          <Link to={allHref} className="text-sm font-medium text-blue-dark hover:underline">
            See all {entries.length} entries
          </Link>
        </div>
      ) : null}
    </WidgetCard>
  );
}
